import React, { FunctionComponent, useEffect, useState } from 'react'
import { Alert, Button, Image, Modal, Pagination, Rate, Space, Table, Tag, Tooltip, Typography } from 'antd'
import moment from 'moment'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { InfoCircleFilled, ClockCircleOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDots } from '@fortawesome/free-solid-svg-icons'
import QueryString from 'query-string'
import SpinLoader from '../../../helpers/SpinLoader'
import { useCallsListContext } from '../../../../context/call/CallsListProvider'
import { Brand, IUser, TCallList, TCallListUser } from '../../../../context/types'
import { useChatContext } from '../../../../context/chat'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../../constants'
import CustomModal from '../../../helpers/CustomModal'
import CallDetails from './CallDetails'
import { useAuthContext } from '../../../../context/auth'
import Reschedule from './Reschedule'
import Cancel from './Cancel'
import ReviewModal from '../../../helpers/ReviewModal/ReviewModal'
import { BrandKey, brands } from '../../../../config'
import logo from '../../../../images/icons/logo.png'

import './index.less'
import { useAccountContext } from '../../../../context/account'

type UrlParam = {
	accountId: string
}
const { Title, Text, Link: AntLink } = Typography
const { Column } = Table

function CallsList() {
	const { auth } = useAuthContext()
	const { callsList, actions } = useCallsListContext()
	const { actions: chatActions } = useChatContext()
	const { search } = useLocation()
	const { account } = useAccountContext()
	const history = useHistory()
	const { accountId } = useParams() as UrlParam

	const [callDetails, setCallDetails] = useState<TCallList | null>(null)
	const [callReschedule, setCallReschedule] = useState<TCallList | null>(null)
	const [callCancel, setCallCancel] = useState<TCallList | null>(null)

	const [showThanksResult, setShowThanksResult] = useState(search === '?thank_you')

	const { reschedule_id, cancel_id } = QueryString.parse(history.location.search, {
		arrayFormat: 'comma',
		parseNumbers: true,
	})

	useEffect(() => {
		if ((!!account?.token && !account?.loading && !!accountId) || !accountId) {
			const token = account?.token && !!accountId ? account.token : null
			actions.getUpcoming(DEFAULT_PAGE, DEFAULT_PAGE_SIZE, token)
			actions.getPast(DEFAULT_PAGE, DEFAULT_PAGE_SIZE, token)
			actions.getCancelled(DEFAULT_PAGE, DEFAULT_PAGE_SIZE, token)
		}
	}, [account])

	useEffect(() => {
		if (!!callDetails) {
			setCallDetails(null)
		}
	}, [callsList?.past])

	useEffect(() => {
		if (reschedule_id && callsList?.upcoming?.list?.length) {
			const call = callsList?.upcoming?.list.find((c: TCallList) => c.id === reschedule_id)
			setCallReschedule(call)
			history.replace({ search: '' })
			setShowThanksResult(false)
		} else if (cancel_id && callsList?.upcoming?.list?.length) {
			const call = callsList?.upcoming?.list.find((c: TCallList) => c.id === cancel_id)
			setCallCancel(call)
			history.replace({ search: '' })
			setShowThanksResult(false)
		}
	}, [reschedule_id, cancel_id, callsList?.upcoming?.list?.length])

	const renderBrand = (brand: Partial<Brand>) => {
		const host = new URL(brand.root_url).hostname.replace('app.', '').replace('dev.', '').split('.')[0] as BrandKey

		return (
			<Image style={{ width: 45 }} src={brands[host].logoUrl || logo} preview={false} referrerPolicy="no-referrer" />
		)
	}
	const renderChannel = (channel?: string) => {
		return channel ? (
			<span>{channel}</span>
		) : (
			<Tag color="#f5f5f5" style={{ color: '#595959', borderColor: '#595959' }}>
				{'NA'}
			</Tag>
		)
	}
	const renderStartTime = (startTime: string) => moment(startTime).tz(auth?.user?.timezone).format('L')
	const renderDuration = (duration: number, record: TCallList) =>
		`${moment(record.start_time).tz(auth?.user?.timezone).format('h:mm')} - ${moment(record.start_time)
			.tz(auth?.user?.timezone)
			.add(duration, 'minute')
			.format('h:mm A')} ${moment.tz(auth?.user?.timezone).format('z')}`

	const UserLink = ({ user }: { user: TCallListUser }) =>
		user.isExpert && user.expert_slug ? (
			<Link to={`/expert/${user?.expert_slug}`}>
				{user?.first_name} {user?.last_name}
			</Link>
		) : (
			<span>
				{user?.first_name} {user?.last_name}
			</span>
		)

	const renderUser = (user: TCallListUser) =>
		user.isExpert ? (
			<span>
				me &#8594; <UserLink user={user} />
			</span>
		) : (
			<span>
				<UserLink user={user} /> &#8594; me
			</span>
		)

	const handleChat = (userId: number) => () => chatActions.getOrCreateChat(userId)
	const renderMessage = (user: TCallListUser) => (
		<Button
			type="link"
			size="large"
			onClick={handleChat(user.id)}
			icon={<FontAwesomeIcon icon={faCommentDots} color="#0067A8" />}
		/>
	)

	const onReschedule = (record: TCallList) => () => {
		setCallReschedule(record)
		history.replace({ search: '' })
		setShowThanksResult(false)
	}

	const onCancel = (record: TCallList) => () => {
		setCallCancel(record)
		history.replace({ search: '' })
		setShowThanksResult(false)
	}

	const onResendInvite = (record: TCallList) => () => {
		const token = account?.token && !!accountId ? account.token : null
		actions.resendInvite(record.id, token)
	}

	const renderActions = (upcoming: boolean) => (id: number, record: TCallList) =>
		upcoming ? (
			<div className="upcoming-actions">
				{record.call_link && (
					<Button
						type="link"
						href={record.call_link}
						target="_blank"
						rel="noreferrer"
						style={{ marginTop: '1px' }}
						icon={
							new Date(record.start_time) < new Date() ? (
								<Tooltip title="Active call session">
									<ClockCircleOutlined style={{ color: '#159b04' }} />
								</Tooltip>
							) : null
						}
					>
						Join Call
					</Button>
				)}
				<Button type="link" onClick={onResendInvite(record)}>
					Resend Invite
				</Button>
				<Button type="link" onClick={onReschedule(record)}>
					Reschedule
				</Button>
				<Button type="link" onClick={onCancel(record)}>
					Cancel
				</Button>
			</div>
		) : null
	const clickDetails = (record: TCallList) => () => setCallDetails(record)
	const renderDetails = (upcoming: boolean) => (record: TCallList) => (
		<Button icon={<InfoCircleFilled />} type={upcoming ? 'link' : 'text'} onClick={clickDetails(record)}>
			Details
		</Button>
	)

	const renderStatus = (record: TCallList) => {
		if (record.expert_accepted_on) {
			return <Text style={{ color: '#429408' }}>Accepted</Text>
		} else if (record.cancelled_on) {
			return <Text type="danger">Cancelled</Text>
		}

		return <Text>Pending</Text>
	}

	const onPageChange = (upcoming: boolean) => (newPage: number, newPageSize: number) => {
		const token = account?.token && !!accountId ? account.token : null
		upcoming ? actions.getUpcoming(newPage - 1, newPageSize, token) : actions.getPast(newPage - 1, newPageSize, token)
	}
	const renderTotal = (total: number) => `Total: ${total}`

	const cancelCallDetails = () => setCallDetails(null)
	const cancelCallReschedule = () => setCallReschedule(null)
	const cancelCallCancel = (isShowModal?: boolean) => {
		setCallCancel(null)
		if (isShowModal) {
			Modal.info({
				content: (
					<Text>
						Email <AntLink href="mailto:refunds@wiseher.com">refunds@wiseher.com</AntLink> or use the chat in the bottom
						right if they need a refund.
					</Text>
				),
				maskClosable: true,
				okText: 'Close',
				okButtonProps: {
					shape: 'round',
				},
			})
		}
	}

	const renderRating = (record: TCallList) => {
		return (
			<ReviewBlock
				record={record}
				onAction={() => {
					actions.getPast()
				}}
			/>
		)
	}

	return (
		<div>
			<Title level={3}>My Calls</Title>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				{showThanksResult && (
					<Alert
						message="Thank you for booking a call"
						description="Please check your email (and spam folder) for the invite."
						type="success"
					/>
				)}
				<SpinLoader loading={callsList.upcoming.loading}>
					<Title level={4}>Upcoming</Title>
					<Table
						showHeader={false}
						dataSource={callsList.upcoming.list}
						rowClassName="calls-table-row"
						className="calls-table"
						rowKey="id"
						size="small"
						pagination={false}
						scroll={{ x: true }}
					>
						<Column className="calls-table-cell" dataIndex="brand" render={renderBrand} />
						<Column className="calls-table-cell" dataIndex="channel" render={renderChannel} />
						<Column className="calls-table-cell" dataIndex="start_time" render={renderStartTime} />
						<Column className="calls-table-cell" dataIndex="planned_duration" render={renderDuration} />
						<Column className="calls-table-cell" dataIndex="user" key="user" render={renderUser} />
						<Column className="calls-table-cell" dataIndex="user" render={renderMessage} />
						<Column className="calls-table-cell" dataIndex="id" render={renderActions(true)} />
						<Column className="calls-table-cell" key="status" render={renderStatus} />
						<Column className="calls-table-cell" key="details" render={renderDetails(true)} />
					</Table>
					<Pagination
						showTotal={renderTotal}
						current={+callsList.upcoming.page + 1}
						total={callsList.upcoming.total}
						pageSize={callsList.upcoming.pageSize}
						pageSizeOptions={[`${DEFAULT_PAGE_SIZE}`, `${2 * DEFAULT_PAGE_SIZE}`, `${4 * DEFAULT_PAGE_SIZE}`]}
						showSizeChanger
						onChange={onPageChange(true)}
					/>
				</SpinLoader>
				<SpinLoader loading={callsList.past.loading}>
					<Title level={4}>Past</Title>
					<Table
						showHeader={false}
						dataSource={callsList.past.list}
						rowClassName="calls-table-row"
						className="calls-table"
						rowKey="id"
						size="small"
						pagination={false}
						scroll={{ x: true }}
					>
						<Column className="calls-table-cell" dataIndex="brand" render={renderBrand} />
						<Column className="calls-table-cell" dataIndex="channel" render={renderChannel} />
						<Column className="calls-table-cell" dataIndex="start_time" render={renderStartTime} />
						<Column className="calls-table-cell" dataIndex="planned_duration" render={renderDuration} />
						<Column className="calls-table-cell" dataIndex="user" key="user" render={renderUser} />
						<Column className="calls-table-cell" key="details" render={renderDetails(false)} />
						{!account?.token && <Column className="calls-table-cell" render={renderRating} />}
					</Table>
					<Pagination
						showTotal={renderTotal}
						current={+callsList.past.page + 1}
						total={callsList.past.total}
						pageSize={callsList.past.pageSize}
						pageSizeOptions={[`${DEFAULT_PAGE_SIZE}`, `${2 * DEFAULT_PAGE_SIZE}`, `${4 * DEFAULT_PAGE_SIZE}`]}
						showSizeChanger
						onChange={onPageChange(false)}
					/>
				</SpinLoader>
				<SpinLoader loading={callsList.cancelled.loading}>
					<Title level={4}>Cancelled</Title>
					<Table
						showHeader={false}
						dataSource={callsList.cancelled.list}
						rowClassName="calls-table-row"
						className="calls-table"
						rowKey="id"
						size="small"
						pagination={false}
						scroll={{ x: true }}
					>
						<Column className="calls-table-cell" dataIndex="brand" render={renderBrand} />
						<Column className="calls-table-cell" dataIndex="channel" render={renderChannel} />
						<Column className="calls-table-cell" dataIndex="start_time" render={renderStartTime} />
						<Column className="calls-table-cell" dataIndex="planned_duration" render={renderDuration} />
						<Column className="calls-table-cell" dataIndex="user" key="user" render={renderUser} />
						<Column className="calls-table-cell" key="details" render={renderDetails(false)} />
						{!account?.token && <Column className="calls-table-cell" render={renderRating} />}
					</Table>
					<Pagination
						showTotal={renderTotal}
						current={+callsList.cancelled.page + 1}
						total={callsList.cancelled.total}
						pageSize={callsList.cancelled.pageSize}
						pageSizeOptions={[`${DEFAULT_PAGE_SIZE}`, `${2 * DEFAULT_PAGE_SIZE}`, `${4 * DEFAULT_PAGE_SIZE}`]}
						showSizeChanger
						onChange={onPageChange(false)}
					/>
				</SpinLoader>
			</Space>
			<CustomModal visible={!!callDetails} onCancel={cancelCallDetails} width={900}>
				<CallDetails record={callDetails} />
			</CustomModal>
			<CustomModal visible={!!callReschedule} onCancel={cancelCallReschedule} width={900} destroyOnClose>
				<Reschedule call={callReschedule} onCancel={cancelCallReschedule} />
			</CustomModal>
			<CustomModal visible={!!callCancel} onCancel={() => cancelCallCancel()} width={900} destroyOnClose>
				<Cancel call={callCancel} onCancel={cancelCallCancel} />
			</CustomModal>
		</div>
	)
}

export default CallsList

// Parts

interface ReviewBlockProps {
	record: TCallList
	onAction: () => void
}

const ReviewBlock: FunctionComponent<ReviewBlockProps> = ({ record, onAction }) => {
	const [isReviewModalVisible, setIsReviewModalVisible] = useState(false)

	if (record.expertReview[0])
		return (
			<Rate
				className="expert-rate"
				value={record.expertReview[0].rating || 0}
				disabled
				style={{ opacity: record.expertReview[0].responce || record.user.isExpert ? 0.3 : 1 }}
			/>
		)

	if (record.user.isExpert)
		return (
			<>
				<Button type="link" onClick={() => setIsReviewModalVisible(true)}>
					Leave Review
				</Button>
				{isReviewModalVisible && (
					<ReviewModal
						expertUser={record.user as IUser}
						call_id={record.id}
						call_name={moment(record.start_time).format('ll') + ' - ' + record.reason}
						visible={isReviewModalVisible}
						onAction={onAction}
						onCancel={() => setIsReviewModalVisible(false)}
					/>
				)}
			</>
		)

	return <></>
}
